import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { TemplateObjectType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import TemplatesPublicGridComponent from 'webapp/components/templates/public/grid';

import type Template from 'webapp/models-custom/template';
import BoardRecord from 'webapp/models/board';
import type BoardLoopRecord from 'webapp/models/board-loop';

export default class BoardsPublicTemplatesRouteTemplate extends Component {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;

	get model(): Template[] {
		return [
			...this.mmTemplatesService.boardPublicTemplateModels,
			...this.mmTemplatesService.boardLoopPublicTemplateModels,
		];
	}

	@action
	previewTemplate(model: Template): void {
		this.navigationService.transitionToRoute('boards.public-templates.template', model);
	}

	@action
	useTemplate(model: Template): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const afterUse = (record: BoardRecord | BoardLoopRecord) => {
			const isTemplatesPath =
				this.navigationService.activatedRoute.includes('boards.public-templates');

			const routeName =
				model.objectType === TemplateObjectType.BOARD ? 'boards.index' : 'boards.loops';

			let afterTransition = () => {
				/* do nothing */
			};

			if (isTemplatesPath && !this.popupService.anyPopupsOpen) {
				afterTransition = () => {
					if (record instanceof BoardRecord) {
						const boardModel = this.mmBoardsService.createModel(record);
						this.mmBoardsService.openPreview(boardModel, null);
					} else {
						const boardLoopModel = this.mmBoardLoopsService.createModel(record);
						this.mmBoardLoopsService.openPreviewPopup(boardLoopModel);
					}
				};
			}

			this.navigationService.transitionToRoute(routeName).then(afterTransition);
		};

		this.mmTemplatesService.trackEvent(model, TrackingEvent.USE_A_TEMPLATE);

		if (model.objectType === TemplateObjectType.BOARD) {
			this.mmBoardsService.createBoardFromTemplate(model, 'public_template', afterUse);
		} else {
			this.mmBoardLoopsService.createBoardLoopFromTemplate(
				model,
				'public_template_loop',
				afterUse,
			);
		}
	}

	<template>
		<div id="public-templates" class="view-grid">
			<TemplatesPublicGridComponent
				@model={{this.model}}
				@previewTemplate={{this.previewTemplate}}
				@useTemplate={{this.useTemplate}}
			/>
		</div>
	</template>
}
